import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Project } from 'shared/types';
import { ProjectFormDialog } from '@/components/dialogs/projects/ProjectFormDialog';
import { AlertCircle, Loader2, Plus } from 'lucide-react';
import ProjectCard from '@/components/projects/ProjectCard.tsx';
import { useKeyCreate, Scope } from '@/keyboard';
import { useProjects } from '@/hooks/useProjects';
import { usePermissions } from '@/hooks/usePermissions';
import { opSyncApi, type OpSyncMapping, type OpConfig } from '@/lib/api';

export function ProjectList() {
  const navigate = useNavigate();
  const { t } = useTranslation('projects');
  const { projects, isLoading, error: projectsError } = useProjects();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('project.create');
  const [error, setError] = useState('');
  const [focusedProjectId, setFocusedProjectId] = useState<string | null>(null);

  // OP sync state
  const [opMappings, setOpMappings] = useState<OpSyncMapping[]>([]);
  const [opBaseUrl, setOpBaseUrl] = useState<string>('');

  const loadOpSync = useCallback(async () => {
    try {
      const [mappings, config] = await Promise.all([
        opSyncApi.getMappings().catch(() => [] as OpSyncMapping[]),
        opSyncApi.getConfig().catch(() => null as OpConfig | null),
      ]);
      setOpMappings(mappings);
      if (config?.base_url) setOpBaseUrl(config.base_url);
    } catch {
      // OP sync not configured — ignore
    }
  }, []);

  useEffect(() => {
    loadOpSync();
  }, [loadOpSync]);

  const handleCreateProject = async () => {
    try {
      const result = await ProjectFormDialog.show({});
      if (result.status === 'saved') return;
    } catch (error) {
      // User cancelled - do nothing
    }
  };

  // Semantic keyboard shortcut for creating new project
  useKeyCreate(canCreate ? handleCreateProject : () => {}, { scope: Scope.PROJECTS });

  const handleEditProject = (project: Project) => {
    navigate(`/settings/projects?projectId=${project.id}`);
  };

  // Set initial focus when projects are loaded
  useEffect(() => {
    if (projects.length === 0) {
      setFocusedProjectId(null);
      return;
    }

    if (!focusedProjectId || !projects.some((p) => p.id === focusedProjectId)) {
      setFocusedProjectId(projects[0].id);
    }
  }, [projects, focusedProjectId]);

  // Helper: find OP sync mapping for a project
  const getOpMapping = (projectId: string) =>
    opMappings.find(
      (m) => m.vk_entity_type === 'project' && m.vk_entity_id === projectId
    );

  return (
    <div className="space-y-6 p-8 pb-16 md:pb-8 h-full overflow-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
        {canCreate && (
          <Button onClick={handleCreateProject}>
            <Plus className="mr-2 h-4 w-4" />
            {t('createProject')}
          </Button>
        )}
      </div>

      {(error || projectsError) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error || projectsError?.message || t('errors.fetchFailed')}
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {t('loading')}
        </div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
              <Plus className="h-6 w-6" />
            </div>
            <h3 className="mt-4 text-lg font-semibold">{t('empty.title')}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {canCreate ? t('empty.description') : 'You have no projects assigned. Contact an admin to get access.'}
            </p>
            {canCreate && (
              <Button className="mt-4" onClick={handleCreateProject}>
                <Plus className="mr-2 h-4 w-4" />
                {t('empty.createFirst')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              isFocused={focusedProjectId === project.id}
              setError={setError}
              onEdit={handleEditProject}
              opSyncMapping={getOpMapping(project.id)}
              opBaseUrl={opBaseUrl}
            />
          ))}
        </div>
      )}
    </div>
  );
}
